<script setup lang="ts">
import BatchNumber from '@/components/features/batches/components/BatchNumber.vue'
import Button from 'primevue/button'
import { Icon } from '@iconify/vue'
import Tag from 'primevue/tag'
import Dialog from 'primevue/dialog'
import Timeline from 'primevue/timeline'
import { ref, computed, onMounted } from 'vue'
import { useRouteId } from '@/composables/useRouteId'
import { getBatchById, getBatchList, getBatchSnapshot } from '@/api/batches'
import { getProductList } from '@/api/products'
import type { Roll } from "@/types/roll-types"
import type { Batch, ReturnedBatch } from "@/types/batch-types"
import type { Product } from '@/types/product-types'
import EditOffsiteForm from '@/components/features/batches/forms/EditOffsiteForm.vue'
import { useToast } from 'primevue/usetoast'
import { formatedDate } from '@/constants'
import { BatchStateValue } from '@/types/batch-types'
import type { Supplier } from '@/types/supplier-types'
import { getSupplierById } from '@/api/suppliers'

export type BatchAction = {
  label: string
  icon: string
  click: () => void
  outlined?: boolean
}

const props = defineProps({
  batch: {
    type: Object as () => Batch | null,
    default: null
  },
  batchId: {
    type: String,
    default: null
  },
  actions: {
    type: Array as () => BatchAction[],
    default: () => []
  },
  isReturnedBatch: {
    type: Boolean,
    default: false
  },
  returnedBatchData: {
    type: Object as () => ReturnedBatch,
    default: null
  },
  previousBatchData: {
    type: Object as () => Batch | null,
    default: null
  }
})

const routeId = useRouteId()
const currentBatchId = computed(() => props.batchId || routeId)
const batchList = ref<any>([])
const previousBatch = ref<Batch | null>(null)
const products = ref<Product[]>([])
const toast = useToast()
const supplier = ref<Supplier>()

const showHistoryModal = ref(false)
const historicalBatchData = ref<Batch | null>(null)
const historyLoading = ref(false)

const showTimelineModal = ref(false)
const timelineLoading = ref(false)
const combinedTimelineActions = ref<any[]>([])
const originalBatch = props.batch || props.previousBatchData || previousBatch.value;
const finalBatch = props.isReturnedBatch ? props.returnedBatchData : null;

const getQualityNameById = (qualityId?: string) => {
  if (!qualityId || !products.value?.length) {
    return qualityId
  }
  const product = products.value.find((p: Product) => p.id === qualityId)
  return product ? product.unfinishedReference : 'Quality not found'
}

const timelineActions = computed(() => {
  const actions = [];

  const cleanDetails = (details: Record<string, any>) => {
    return Object.entries(details)
      .filter(([_, value]) => value !== null && value !== undefined && value !== '')
      .reduce((obj, [key, value]) => {
        obj[key] = value;
        return obj;
      }, {} as Record<string, any>);
  };

  if (originalBatch?.dateKnitted) {
    const details = {
      'Quality': getQualityNameById(originalBatch.quality?.name),
      'Knitting Supplier': supplier.value?.name || 'Knitting Supplier not found',
      'Machine Number': originalBatch.machineNumber || 'Machine Number not found',
      'Bay': originalBatch.bay || 'Bay not found',
      'Total Weight': originalBatch.weight ? `${originalBatch.weight} kg` : 'Total Weight not found'
    };
    actions.push({
      status: 'Batch Knitted',
      date: formatedDate(originalBatch.dateKnitted),
      icon: 'pi pi-check',
      color: '#607D8B',
      batchType: 'original',
      details: cleanDetails(details)
    });
  }

  if ((originalBatch?.state === BatchStateValue.OFFSITE || finalBatch) && originalBatch && originalBatch.dateSentToSupplier) {
    const details = {
      'Finishing Supplier': supplier.value?.name,
      'Quality': getQualityNameById(originalBatch.quality?.name)
    };
    actions.push({
      status: 'Sent to Supplier',
      date: formatedDate(originalBatch.dateSentToSupplier),
      icon: 'pi pi-truck',
      color: '#FF9800',
      batchType: 'original',
      details: cleanDetails(details)
    });
  }

  if (finalBatch?.dateReturned) {
    const details = {
      'Total Length': finalBatch.totalLengthValue ? `${finalBatch.totalLengthValue}` : 'Total Length not found',
      'Total Weight': finalBatch.totalWeight ? `${finalBatch.totalWeight} kg` : 'Total Weight not found'
    };
    actions.push({
      status: 'Returned from Supplier',
      date: formatedDate(finalBatch.dateReturned),
      icon: 'pi pi-arrow-left',
      color: '#673AB7',
      batchType: 'returned',
      details: cleanDetails(details)
    });
    actions.push({
      status: 'Batch Finished',
      date: formatedDate(finalBatch.dateReturned),
      icon: 'pi pi-flag-fill',
      color: '#4CAF50',
      batchType: 'returned'
    });
  }

  if (finalBatch?.dateDispatched) {
    actions.push({
      status: 'Dispatched to Customer',
      date: formatedDate(finalBatch.dateDispatched),
      icon: 'pi pi-box',
      color: '#03A9F4',
      batchType: 'returned'
    })
  }

  return actions.sort((a, b) => new Date(a.date || 0).getTime() - new Date(b.date || 0).getTime());
});


const batchNumber = computed(() => {
  if (props.isReturnedBatch && props.returnedBatchData) {
    return props.returnedBatchData.batchNumber || 'Batch not found';
  }
  if (props.batch) {
    return props.batch.batchNumber || 'Batch not found';
  }
  const foundBatch = batchList.value.find((b: { id: string }) => b.id === currentBatchId.value);
  return foundBatch ? foundBatch.batchNumber : 'Batch not found';
})

const previousBatchNumber = computed(() => {
  if (props.isReturnedBatch && (props.previousBatchData || previousBatch.value)) {
    return (props.previousBatchData || previousBatch.value)?.batchNumber;
  }
  return null;
})

const formattedState = computed(() => {
  if (props.isReturnedBatch && props.returnedBatchData) {
    const hasPassedQualityCheck = props.returnedBatchData.qualityChecks?.some((qc: any) => qc.qualityCheckStatus === 'Passed');
    const hasPassedFireTest = props.returnedBatchData.fireTests?.some((ft: any) => ft.fireTestingStatus === 'Passed');

    if (hasPassedQualityCheck && hasPassedFireTest) {
      return 'Available';
    } else if (props.returnedBatchData.dateDispatched) {
      return 'Dispatched';
    } else {
      return 'Finished';
    }
  }

  const sourceBatch = props.batch || batchList.value.find((b: { id: string }) => b.id === currentBatchId.value);
  if (!sourceBatch) return 'Unknown State';

  if (sourceBatch.rolls && sourceBatch.rolls.length > 0) {
    const activeRolls = sourceBatch.rolls.filter((roll: Roll) => roll.isActive);
    if (activeRolls.length > 0) {
      const uniqueRollStates = new Set(activeRolls.map((roll: Roll) => roll.rollState));
      if (uniqueRollStates.size > 1) {
        return 'Mixed';
      }
    }
  }

  switch (sourceBatch.state) {
    case 0: return 'Greige';
    case 1: return 'Offsite';
    case 2: return 'Finished';
    case 3: return 'Available';
    case 4: return 'Dispatched';
    default: return 'Unknown State';
  }
})

const fetchPreviousBatch = async () => {
  if (props.isReturnedBatch && props.returnedBatchData?.previousBatchId) {
    try {
      previousBatch.value = await getBatchById(props.returnedBatchData.previousBatchId);
    } catch (error) {
      console.error("Error fetching previous batch:", error);
    }
  }
}

const viewHistoryModal = async () => {
  const id = props.returnedBatchData?.previousBatchId;
  if (!id) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'No previous batch data available',
      life: 5000
    });
    return;
  }

  historyLoading.value = true;
  showHistoryModal.value = true;

  try {
    const historyData = await getBatchSnapshot(id, 'BeforeReturn');
    historicalBatchData.value = historyData.snapshot;
  } catch (error) {
    console.error('Failed to load batch history:', error);
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to load batch history',
      life: 5000
    });

    showHistoryModal.value = false;
  } finally {
    historyLoading.value = false;
  }
}

const closeHistoryModal = () => {
  showHistoryModal.value = false;
  historicalBatchData.value = null;
}

const openTimelineModal = async () => {
  showTimelineModal.value = true;

  if (props.isReturnedBatch) {
    timelineLoading.value = true;

    try {
      const combinedActions = [...timelineActions.value];

      combinedTimelineActions.value = combinedActions;
    } catch (error) {
      console.error('Failed to load complete timeline:', error);
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to load complete batch timeline',
        life: 3000
      });
    } finally {
      timelineLoading.value = false;
    }
  } else {
    combinedTimelineActions.value = timelineActions.value;
  }
}

const closeTimelineModal = () => {
  showTimelineModal.value = false;
  combinedTimelineActions.value = [];
}

const hasHistoryData = computed(() => {
  if (props.isReturnedBatch) {
    return !!(props.returnedBatchData?.previousBatchId || props.previousBatchData || previousBatch.value);
  }
  return true;
})

async function  getSupplier(batch: Batch | null): Promise<Supplier | undefined> {
  if (!batch) return undefined;
  const supplierId = batch.finishingSupplierId || batch.knittingSupplierId;
  if (!supplierId) return undefined;
  return await getSupplierById(supplierId);
}

onMounted(async () => {
  if (!props.batch && !props.isReturnedBatch) {
    batchList.value = await getBatchList()
  }

  if (props.isReturnedBatch && !props.previousBatchData) {
    await fetchPreviousBatch();
  }
  if (!props.isReturnedBatch) {
    products.value = await getProductList();
  }
  supplier.value = await  getSupplier(props.batch || props.previousBatchData || previousBatch.value);
})
</script>

<template>
  <div>
    <div class="flex justify-between items-center mb-3">
      <div class="flex items-center gap-3">
        <BatchNumber
          :batch-id="currentBatchId"
          :is-returned-batch="isReturnedBatch"
          :returned-batch-data="returnedBatchData"
        />
        <Tag
          :severity="formattedState === 'Mixed' ? 'warning' : 'secondary'"
          :class="formattedState === 'Mixed' ? 'bg-yellow-100 text-yellow-700 rounded-lg !px-3' : 'bg-gray-100 text-gray-500 rounded-lg !px-3'"
          :value="formattedState"
        />
      </div>

      <div class="flex gap-5">
        <Button
          v-for="action in actions"
          :key="action.label"
          :outlined="action.outlined"
          :label="action.label"
          @click="action.click"
          class="min-w-48"
        >
          <template #icon>
            <Icon :icon="action.icon" class="text-xl ml-3 order-1" />
          </template>
        </Button>
      </div>
    </div>

    <div class="flex items-center gap-2 text-sm text-gray-600">
      <template v-if="isReturnedBatch && hasHistoryData">
        <button
          v-if="previousBatchNumber"
          @click="viewHistoryModal"
          class="flex items-center gap-1 hover:text-primary-500 hover:underline transition-colors"
        >
          <Icon icon="mdi:history" class="text-base" />
          <span>Previous Batch: {{ previousBatchNumber }}</span>
        </button>

        <Icon v-if="previousBatchNumber" icon="mdi:chevron-right" class="text-gray-400" />

        <button
          @click="openTimelineModal"
          class="flex items-center gap-1 hover:text-primary-500 hover:underline transition-colors"
        >
          <Icon icon="mdi:timeline-clock-outline" class="text-base" />
          <span>Batch History</span>
        </button>

        <Icon icon="mdi:chevron-right" class="text-gray-400" />
      </template>

      <template v-else>
        <button
          @click="openTimelineModal"
          class="flex items-center gap-1 hover:text-primary-500 hover:underline transition-colors"
        >
          <Icon icon="mdi:timeline-clock-outline" class="text-base" />
          <span>Batch History</span>
        </button>
        <Icon icon="mdi:chevron-right" class="text-gray-400" />
      </template>

      <span class="text-gray-800 font-medium">{{ batchNumber }}</span>
    </div>

    <Dialog
      v-model:visible="showHistoryModal"
      :style="{ width: '95vw', height: '90vh' }"
      :maximizable="true"
      :closable="true"
      :modal="true"
      class="history-modal"
    >
      <template #header>
        <div class="w-full text-center">
          <h2 class="text-2xl font-bold text-gray-800 mb-1">{{ previousBatchNumber }}</h2>
          <div class="flex items-center justify-center gap-2 text-sm text-gray-600">
            <Icon icon="mdi:history" class="text-lg" />
            <span>Historical snapshot before return from supplier</span>
          </div>
        </div>
      </template>

      <div v-if="historyLoading" class="flex justify-center items-center h-64">
        <div class="flex flex-col items-center gap-3">
          <i class="pi pi-spinner pi-spin text-3xl text-primary-500"></i>
          <span class="text-gray-600">Loading batch history...</span>
        </div>
      </div>

      <div v-else-if="historicalBatchData" class="h-full overflow-auto">
        <div class="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
          <div class="flex items-center gap-2 mb-2">
            <Icon icon="mdi:clock-outline" class="text-amber-600 text-xl" />
            <h4 class="font-semibold text-amber-800">Historical Snapshot</h4>
            <Tag value="Offsite" severity="warning" class="!px-2 !py-1" />
          </div>
          <p class="text-amber-700 text-sm">
            This shows how the batch looked when it was <strong>Offsite</strong> at the supplier,
            before being returned and processed into the current finished batch.
          </p>
        </div>

        <EditOffsiteForm
          v-model="historicalBatchData"
          :disabled="true"
          :is-historical="true"
        />
      </div>

      <template #footer>
        <div class="flex justify-end gap-3">
          <Button
            label="Close"
            icon="pi pi-times"
            @click="closeHistoryModal"
            outlined
          />
        </div>
      </template>
    </Dialog>

    <Dialog
      v-model:visible="showTimelineModal"
      :style="{ width: '70vw', height: '85vh', maxWidth: '1000px' }"
      :maximizable="true"
      :closable="true"
      :modal="true"
      class="history-modal"
    >
      <template #header>
        <div class="w-full text-left">
          <h2 class="text-2xl font-bold text-gray-800 mb-1">
            {{ isReturnedBatch ? 'Complete History' : 'History' }} for {{ batchNumber }}
          </h2>
          <div class="flex items-center gap-2 text-sm text-gray-600">
            <Icon icon="mdi:timeline-clock-outline" class="text-lg" />
            <span>
              {{ isReturnedBatch ? 'Complete timeline including previous batch and return process' : 'Timeline of batch actions' }}
            </span>
          </div>

          <div v-if="isReturnedBatch && previousBatchNumber" class="mt-2 text-sm text-gray-500">
            <Icon icon="mdi:information-outline" class="mr-1" />
            Previous batch: {{ previousBatchNumber }}
          </div>
        </div>
      </template>

      <div v-if="timelineLoading" class="flex justify-center items-center h-64">
        <div class="flex flex-col items-center gap-3">
          <i class="pi pi-spinner pi-spin text-3xl text-primary-500"></i>
          <span class="text-gray-600">Loading complete timeline...</span>
        </div>
      </div>

      <div v-else class="h-full overflow-auto pt-4">
        <Timeline :value="combinedTimelineActions.length > 0 ? combinedTimelineActions : timelineActions" align="left">
          <template #marker="slotProps">
            <span
              class="flex w-8 h-8 items-center justify-center text-white rounded-full z-10 shadow-md"
              :style="{ backgroundColor: slotProps.item.color }"
            >
              <i :class="slotProps.item.icon" class="text-lg"></i>
            </span>
          </template>
          <template #content="slotProps">
            <div>
              <div class="flex items-center gap-2">
                <span class="font-bold">{{ slotProps.item.status }}</span>
                <Tag
                  v-if="isReturnedBatch && slotProps.item.batchType"
                  :value="slotProps.item.batchType === 'original' ? 'Original' : 'Returned'"
                  :severity="slotProps.item.batchType === 'original' ? 'info' : 'success'"
                  class="!px-2 !py-1 text-xs"
                />
              </div>
              <div class="text-sm text-gray-500 mt-1">{{ slotProps.item.date }}</div>

              <div
                v-if="slotProps.item.details && Object.keys(slotProps.item.details).length > 0"
                class="mt-3 bg-gray-50 border border-gray-200 rounded-md p-3 text-sm"
              >
                <dl class="space-y-1">
                  <div
                    v-for="(value, key) in slotProps.item.details"
                    :key="key"
                    class="flex"
                  >
                    <dt class="w-36 shrink-0 font-medium text-gray-500">{{ key }}:</dt>
                    <dd class="text-gray-800">{{ value }}</dd>
                  </div>
                </dl>
              </div>
            </div>
          </template>
        </Timeline>
      </div>

      <template #footer>
        <div class="flex justify-end gap-3">
          <Button
            label="Close"
            icon="pi pi-times"
            @click="closeTimelineModal"
            outlined
          />
        </div>
      </template>
    </Dialog>
  </div>
</template>

<style scoped>
.history-modal :deep(.p-dialog-content) {
  padding: 1rem 1.5rem;
  overflow: hidden;
}

.history-modal :deep(.p-dialog-header) {
  padding: 1.5rem 1.5rem 1rem 1.5rem;
}

.history-modal :deep(.p-dialog-footer) {
  padding: 1rem 1.5rem 1.5rem 1.5rem;
}
</style>